<template>
    <page-layout>
        <template #header>
            <page-title title="Deliver Task" @on-close="handleExit" />
        </template>
        <g-loader v-if="isLoading" type="fixed" />
        <template v-else>
            <g-button icon="angle-left" theme="live" size="small" @click="handleExit">Back</g-button>

            <network-connect-reminder
                v-if="showConnectReminder"
                :network-id="task.network_id"
                location="deliver_campagin"
                class="margin-top margin-bottom g-p-0"
            />

            <div v-if="canPullContentAutomatically(task.network_id)">
                <contact-network-item class="margin-top" :contact-network="contactNetwork" context="campaign" />
                <g-typography class="margin-top-small" tag="p" size="small">
                    Your {{ NETWORK_INFO_BY_ID[task.network_id].name }} post and insights should be delivered
                    automatically, but you can still upload your post manually below.
                </g-typography>
            </div>

            <task-deliver-uploaded-content
                v-if="showUploadedContentDelivery"
                :is-loading="isLoading"
                :is-delivering="isDelivering"
                :error-message="errorMessage"
                :errors="errors"
                :network-id="task.network_id"
                :upload-content-url="uploadContentUrl"
                :uploaded-content-delivery-url="uploadedContentDeliveryUrl"
                :delete-uploaded-content-url="deleteUploadedContentUrl"
                :upload-headers="uploadHeaders"
                :has-uploaded-files="hasUploadedFiles"
                :uploaded-files="uploadedFiles"
                :is-file-uploading="isFileUploading"
                :deliver-uploaded-content="deliverUploadedContent"
                @file-change="handleFileChange"
                @reset-uploads="handleResetUploads"
                @upload-failed="handleUploadFailed"
                @is-ready="handleUploadedContentIsReady"
                @deliver-uploaded-content="handleDeliverUploadedContent"
                @deliver-failed="handleDeliverUploadedContentFailed"
                @exit="handleExit"
            />
            <div v-else>
                <error-message-card
                    v-if="errorMessage && !errors.url"
                    :message="errorMessage"
                    class="margin-top"
                    :color="getErrorColor"
                />
                <div class="margin-top">
                    <g-form>
                        <g-form-item :errors="errors.url" label="Paste the link to your post">
                            <g-input v-model="url" placeholder="Paste the link to your content here" size="large" />
                        </g-form-item>

                        <g-form-item v-if="isMeta" label="Date of Post">
                            <g-date-picker v-model="postedAt" class="date-picker" size="large" />
                        </g-form-item>
                    </g-form>
                </div>
            </div>
        </template>
        <template #footer>
            <g-button
                type="primary"
                shape="round"
                size="large"
                theme="live"
                :disabled="isDisabled"
                :loading="isDelivering || deliverUploadedContent"
                block
                @click="handleDeliver"
            >
                Deliver
            </g-button>
        </template>
    </page-layout>
</template>

<script>
    import { ref, computed, toRefs } from 'vue-demi';
    import { NETWORK_INFO_BY_ID } from '../../../constants/networks';
    import PageLayout from '../../layouts/PageLayout.vue';
    import PageTitle from '../../library/PageTitle.vue';
    import ErrorMessageCard from './partials/ErrorMessageCard.vue';
    import TaskDeliverUploadedContent from '../activation-briefs/partials/tasks/TaskDeliverUploadedContent.vue';
    import ContactNetworkItem from '../../data-views/networks/partials/ContactNetworkItem.vue';
    import NetworkConnectReminder from '../../../../src/components/sections/networks/NetworkConnectReminder.vue';
    import useNetworks from '../../../../src/composables/use-networks';
    import { useFeatureFlagsStore } from '../../../../stores';

    export default {
        name: 'TaskDeliveryView',

        components: {
            PageLayout,
            PageTitle,
            ErrorMessageCard,
            TaskDeliverUploadedContent,
            NetworkConnectReminder,
            ContactNetworkItem
        },
        props: {
            isLoading: {
                type: Boolean
            },
            isDelivering: {
                type: Boolean
            },
            errorMessage: {
                type: String
            },
            errors: {
                type: [Array, Object]
            },
            errorCode: {
                type: Number
            },
            task: {
                type: Object,
                default: null
            },
            brandNetworks: {
                type: Array,
                default: null
            },
            uploadContentUrl: {
                type: String,
                default: ''
            },
            uploadedContentDeliveryUrl: {
                type: String,
                default: ''
            },
            deleteUploadedContentUrl: {
                type: String,
                default: ''
            },
            uploadHeaders: {
                type: Object,
                default: null
            },
            hasUploadedFiles: {
                type: Boolean,
                default: false
            },
            uploadedFiles: {
                type: Array,
                default: null
            },
            isFileUploading: {
                type: Boolean,
                default: false
            }
        },

        setup(props, { emit }) {
            const featureFlags = useFeatureFlagsStore().data;
            const { errorCode, task, brandNetworks, isFileUploading } = toRefs(props);
            const url = ref(null);
            const postedAt = ref(null);
            const deliverUploadedContent = ref(false);
            const uploadedContentIsReady = ref(false);

            const isDisabled = computed(() => {
                if (showUploadedContentDelivery.value) {
                    return !uploadedContentIsReady.value || isFileUploading.value || deliverUploadedContent.value;
                } else {
                    return !url.value || (isMeta.value && !postedAt.value);
                }
            });

            const {
                getContactNetworkById,
                isTiktokById,
                isPinterestById,
                isMetaUploadContentDeliveryNetworkBySlugAndMediaType,
                canPullContentAutomatically,
                showUploadedContentDeliveryForNetwork
            } = useNetworks();

            const contactNetwork = computed(() => getContactNetworkById(task.value?.network_id));
            const isMeta = computed(() =>
                isMetaUploadContentDeliveryNetworkBySlugAndMediaType(task.value?.network?.slug, task.value?.media_type)
            );
            const showConnectReminder = computed(
                () => isMeta.value || isTiktokById(task.value?.network_id) || isPinterestById(task.value?.network_id)
            );
            const showUploadedContentDelivery = computed(() =>
                showUploadedContentDeliveryForNetwork(
                    task.value?.network_id,
                    contactNetwork.value,
                    brandNetworks.value,
                    null,
                    task.value?.network?.slug,
                    task.value?.media_type
                )
            );

            const getErrorColor = computed(() => {
                return errorCode.value === 423 ? 'green' : null;
            });

            const handleDeliver = () => {
                if (showUploadedContentDelivery.value) {
                    deliverUploadedContent.value = true;
                } else {
                    emit('deliver', url.value, postedAt.value);
                }
            };

            const handleDeliverUploadedContent = () => {
                deliverUploadedContent.value = false;
            };

            const handleDeliverUploadedContentFailed = () => {
                deliverUploadedContent.value = false;
            };

            const handleExit = () => {
                emit('exit');
            };

            const handleFileChange = (info) => {
                emit('file-change', info);
            };

            const handleResetUploads = () => {
                emit('reset-uploads');
            };

            const handleUploadFailed = () => {
                emit('upload-failed');
            };

            const handleUploadedContentIsReady = (isReady) => {
                uploadedContentIsReady.value = isReady;
            };

            return {
                url,
                postedAt,
                getErrorColor,
                isDisabled,
                isMeta,
                canPullContentAutomatically,
                showConnectReminder,
                showUploadedContentDelivery,
                deliverUploadedContent,
                contactNetwork,
                handleDeliver,
                handleExit,
                handleFileChange,
                handleResetUploads,
                handleUploadFailed,
                handleDeliverUploadedContent,
                handleDeliverUploadedContentFailed,
                handleUploadedContentIsReady,
                NETWORK_INFO_BY_ID
            };
        }
    };
</script>

<style lang="scss" scoped>
    ::v-deep .ant-form-item {
        margin-bottom: $g-spacing-4;
    }

    .margin-top-small {
        margin-top: $g-spacing-4;
    }

    .margin-top {
        margin-top: $g-spacing-6;
    }

    .margin-bottom {
        margin-bottom: $g-spacing-6;
    }

    .date-picker {
        width: 100%;
    }
</style>
